import React from 'react';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert } from '@mui/material';
import {
    IndicatorTypeLabels, TimeFrameLabels,
    MarketRegimeTypeLabels, MarketRegimeTypeColors,
    VolRegimeTypeLabels, VolRegimeTypeColors,
    TrendDirectionLabels, TrendDirectionColors,
    TailRiskLevelLabels, TailRiskLevelColors,
    SignalDirectionLabels, SignalDirectionColors, SignalStrengthLabels,
    FactorTypeLabels, FactorTypeColors,
} from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SignalChip = ({ signal }) => {
    const colorMap = { BUY: 'success', SELL: 'error', NEUTRAL: 'default', LONG_VOL: 'success', SHORT_VOL: 'error' };
    return <Chip label={signal || '-'} size="small" color={colorMap[signal] || 'default'} />;
};

const TechnicalsTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <TabLoading />;

    const indicators = data.indicators || [];
    const regimeDetections = data.regime_detections || [];
    const volRegimes = data.vol_regimes || [];
    const trendReversions = data.trend_reversions || [];
    const tailRisks = data.tail_risks || [];
    const signals = data.signals || [];
    const factorScores = data.factor_scores || [];
    const correlations = data.correlations || [];

    return (
        <Box>
            {/* Indicators */}
            <SectionTitle>Indicators</SectionTitle>
            {indicators.length === 0 ? <EmptyState message="No indicators" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Indicator</TableCell>
                                <TableCell>Time Frame</TableCell>
                                <TableCell align="right">Value</TableCell>
                                <TableCell>Signal</TableCell>
                                <TableCell>Updated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {indicators.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{IndicatorTypeLabels[row.indicator_type] || row.indicator_type}</TableCell>
                                    <TableCell>{TimeFrameLabels[row.time_frame] || row.time_frame || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.latest_value)}</TableCell>
                                    <TableCell><SignalChip signal={row.latest_signal} /></TableCell>
                                    <TableCell>{formatDateTime(row.updated_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Regime Detection */}
            <SectionTitle>Market Regime Detection</SectionTitle>
            {regimeDetections.length === 0 ? <EmptyState message="No regime detection data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Detected Regime</TableCell>
                                <TableCell align="right">Probability</TableCell>
                                <TableCell>Detection Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {regimeDetections.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={MarketRegimeTypeLabels[row.detected_regime] || row.detected_regime} size="small" color={MarketRegimeTypeColors[row.detected_regime] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatPercent(row.regime_probability)}</TableCell>
                                    <TableCell>{formatDate(row.detection_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Vol Regime */}
            <SectionTitle>Volatility Regime</SectionTitle>
            {volRegimes.length === 0 ? <EmptyState message="No vol regime data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Vol Regime</TableCell>
                                <TableCell align="right">Percentile</TableCell>
                                <TableCell>Classification Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {volRegimes.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={VolRegimeTypeLabels[row.vol_regime] || row.vol_regime} size="small" color={VolRegimeTypeColors[row.vol_regime] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatPercent(row.vol_percentile)}</TableCell>
                                    <TableCell>{formatDate(row.classification_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Trend Reversion */}
            <SectionTitle>Trend &amp; Mean Reversion</SectionTitle>
            {trendReversions.length === 0 ? <EmptyState message="No trend reversion data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Trend Direction</TableCell>
                                <TableCell align="right">Strength</TableCell>
                                <TableCell align="right">Mean Reversion Score</TableCell>
                                <TableCell>Detection Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {trendReversions.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={TrendDirectionLabels[row.trend_direction] || row.trend_direction} size="small" color={TrendDirectionColors[row.trend_direction] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatNumber(row.trend_strength)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.mean_reversion_score)}</TableCell>
                                    <TableCell>{formatDate(row.detection_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Tail Risk */}
            <SectionTitle>Tail Risk</SectionTitle>
            {tailRisks.length === 0 ? <EmptyState message="No tail risk data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Risk Level</TableCell>
                                <TableCell align="right">VaR 95%</TableCell>
                                <TableCell align="right">CVaR 95%</TableCell>
                                <TableCell>Estimation Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tailRisks.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={TailRiskLevelLabels[row.risk_level] || row.risk_level} size="small" color={TailRiskLevelColors[row.risk_level] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatPercent(row.var_95)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.cvar_95)}</TableCell>
                                    <TableCell>{formatDate(row.estimation_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Signals */}
            <SectionTitle>Signals</SectionTitle>
            {signals.length === 0 ? <EmptyState message="No signals" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Direction</TableCell>
                                <TableCell>Strength</TableCell>
                                <TableCell align="right">Confidence</TableCell>
                                <TableCell align="right">Entry Price</TableCell>
                                <TableCell align="right">Target Price</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {signals.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={SignalDirectionLabels[row.direction] || row.direction} size="small" color={SignalDirectionColors[row.direction] || 'default'} />
                                    </TableCell>
                                    <TableCell>{SignalStrengthLabels[row.strength] || row.strength || '-'}</TableCell>
                                    <TableCell align="right">{formatPercent(row.confidence_score)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.entry_price)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.target_price)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Factor Scores */}
            <SectionTitle>Factor Scores</SectionTitle>
            {factorScores.length === 0 ? <EmptyState message="No factor scores" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Factor</TableCell>
                                <TableCell align="right">Normalized Score</TableCell>
                                <TableCell align="right">Weight</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {factorScores.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={FactorTypeLabels[row.factor_type] || row.factor_type} size="small" color={FactorTypeColors[row.factor_type] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatNumber(row.normalized_score)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.weight)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Correlations */}
            <SectionTitle>Correlations</SectionTitle>
            {correlations.length === 0 ? <EmptyState message="No correlation data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol A</TableCell>
                                <TableCell>Symbol B</TableCell>
                                <TableCell align="right">Correlation</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {correlations.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{row.symbol_a || '-'}</TableCell>
                                    <TableCell>{row.symbol_b || '-'}</TableCell>
                                    <TableCell align="right">
                                        <Typography
                                            component="span"
                                            sx={{ color: row.correlation_coefficient >= 0 ? '#22c55e' : '#ef4444', fontWeight: 500 }}
                                        >
                                            {row.correlation_coefficient != null ? Number(row.correlation_coefficient).toFixed(4) : '-'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default TechnicalsTab;
